import { styleConstants, themeConstants } from '@paywithextend/components'
import Button from 'ext-ui/components/Button/Button'
import { spacing } from 'ext-ui/style/spacing'
import moment from 'moment'
import React from 'react'
import { useDispatch } from 'react-redux'
import { createSetIsToastOpen } from 'src/app/Toast/redux/toast.actions'
import Box from 'src/components/Box/Box'
import { ButtonDesigns, ButtonTypes } from 'src/components/Button/Button'
import ButtonAndMoreDropdown from 'src/components/ButtonAndMoreDropdown/ButtonAndMoreDropdown'
import HoverableLink from 'src/components/HoverableLink/HoverableLink'
import { MoreDropdownDesigns } from 'src/components/MoreDropdown/MoreDropdown'
import SvgCancelIcon from 'src/components/Svgs/SvgCancelIcon'
import SvgChevronDownLoD from 'src/components/Svgs/SvgChevronDownLoD'
import SvgChevronUpLoD from 'src/components/Svgs/SvgChevronUpLoD'
import SvgExternalLink from 'src/components/Svgs/SvgExternalLink'
import SvgPencilIcon from 'src/components/Svgs/SvgPencilIcon'
import SvgRecurring from 'src/components/Svgs/SvgRecurring'
import Trns, { useTranslate } from 'src/localization/Trns'
import {
  BillingCycle,
  getSaasTierLabel,
  SaasTierType,
  SubscriptionStatus,
} from 'src/models/organizations.models'
import { connectedPush } from 'src/navigation/Lnk'
import { CANCEL_SUBSCRIPTION_ROUTE, PRICING_ROUTE } from 'src/navigation/urls'
import {
  usePutManageSubscription,
  usePutRenewSubscriptionRequest,
  useQueryOrganizationSaasTier,
  useQueryOrganizationSubscription,
} from 'src/queries/organizations/saas.queries'
import { css } from 'styled-components'

import styles from './ManageSubscriptionHeader.style'

const getBillingCycleLabel = (billingCycle: BillingCycle): string => {
  switch (billingCycle) {
    case BillingCycle.MONTHLY:
      return 'Monthly'
    case BillingCycle.QUARTERLY:
      return 'Quarterly'
    case BillingCycle.YEARLY:
      return 'Yearly'
    case BillingCycle.UNKNOWN:
      return 'Unknown'
  }
}

interface ManageSubscriptionHeaderProps {
  organizationId: string
  currentTier: SaasTierType
}
const ManageSubscriptionHeader: React.FC<ManageSubscriptionHeaderProps> = (
  props,
) => {
  const { organizationId, currentTier } = props
  const translate = useTranslate()
  const dispatch = useDispatch()

  const { data: currentPlan } = useQueryOrganizationSaasTier({
    orgId: organizationId,
  })
  const { data: organizationSubscription } = useQueryOrganizationSubscription({
    orgId: organizationId,
  })

  const { mutate: renewSubscription } = usePutRenewSubscriptionRequest()
  const { mutate: putManageSubscription } = usePutManageSubscription()

  const handleCancelSubscription = (): void => {
    dispatch(connectedPush(CANCEL_SUBSCRIPTION_ROUTE(organizationId)))
  }

  const handleRenewSubscription = (): void => {
    renewSubscription(
      { orgId: organizationId, body: {} },
      {
        onSuccess: (data) => {
          dispatch(
            createSetIsToastOpen(true, {
              toastMessage: (
                <span>
                  {translate(
                    'Your subscription has been renewed successfully!',
                  )}
                </span>
              ),
            }),
          )
        },
        onError: (error) => {
          dispatch(
            createSetIsToastOpen(true, {
              toastMessage: (
                <span>
                  {translate(
                    'Oops! There was a problem renewing your subscription. Please try again later or contact customer support.',
                  )}
                </span>
              ),
              failureColorScheme: true,
            }),
          )
        },
      },
    )
  }

  return (
    <div className={'ManageSubscriptionHeader'} css={styles}>
      <div className={'flex-column gap-6'}>
        <div className={'flex-column gap-3'}>
          <span className={'plan-subheader'}>{translate('Current Plan')}</span>
          <span className={'font-h1'}>
            {/*TODO: refactor to move this into feature component so we can have skeleton loader*/}
            {translate('${tier} Plan', {
              tier: getSaasTierLabel(currentPlan?.tier ?? 'STARTER'),
            })}
          </span>
        </div>
        {/* TODO: actual link*/}
        <HoverableLink
          className="flex-row gap-1"
          ariaLabelSource={"What's included in this plan?"}
          href={PRICING_ROUTE()}
        >
          {translate("What's included in this plan?")}
          <SvgExternalLink width={16} height={16} viewBox={'0 0 24 24'} />
        </HoverableLink>
        {currentTier !== 'STARTER' && (
          <ButtonAndMoreDropdown
            buttonProps={{
              onClick: (): void => {
                putManageSubscription({
                  orgId: organizationId,
                  redirect: true,
                  body: {},
                })
              },
              text: (
                <span
                  css={css`
                    color: ${themeConstants.colors.cta};
                  `}
                >
                  <Trns source={'Manage Plan'} />
                </span>
              ),
              design: ButtonDesigns.plainNoShadow,
              type: ButtonTypes.button,
            }}
            moreDropdownProps={{
              alignRight: true,
              design: MoreDropdownDesigns.CtaBlueWhiteBg,
              minWidth: 330,
              verticalBuffer: 8,
              topOptions: [
                {
                  id: 'modifyPlan',
                  key: 'modifyPlan',
                  testId: 'modifyPlan',
                  label: (
                    <div
                      css={css`
                        display: flex;
                        flex-direction: row;
                        align-items: center;
                        gap: 8px;
                        left: -2px;
                        position: relative;
                        width: 100%;
                        font-size: 14px;
                        font-family: ${styleConstants.fontFamilies.regular};
                        color: ${themeConstants.colors.cta};
                      `}
                    >
                      <SvgPencilIcon />
                      <Trns source={'Modify Plan'} />
                    </div>
                  ),
                  ariaLabelSource: 'Modify Plan',
                  onClick: (): void => {
                    putManageSubscription({
                      orgId: organizationId,
                      redirect: true,
                      body: {},
                    })
                  },
                },
              ],
              bottomOptions: [
                ...(organizationSubscription?.subscription?.state.status ===
                'ACTIVE'
                  ? [
                      {
                        id: 'cancelSubscription',
                        testId: 'cancelSubscription',
                        key: 'cancelSubscription',
                        label: (
                          <div
                            css={css`
                              display: flex;
                              flex-direction: row;
                              align-items: center;
                              gap: 8px;
                              left: -2px;
                              position: relative;
                              width: 100%;
                              font-size: 14px;
                              font-family: ${styleConstants.fontFamilies
                                .regular};
                              color: ${styleConstants.colors.red_dark};
                            `}
                          >
                            <SvgCancelIcon />
                            {translate('Cancel Subscription')}
                          </div>
                        ),
                        ariaLabelSource: 'Cancel Subscription',
                        onClick: handleCancelSubscription,
                      },
                    ]
                  : []),
                ...(organizationSubscription?.subscription?.state.status ===
                SubscriptionStatus.NON_RENEWING
                  ? [
                      {
                        id: 'renewSubscription',
                        testId: 'renewSubscription',
                        key: 'renewSubscription',
                        label: (
                          <div
                            css={css`
                              display: flex;
                              flex-direction: row;
                              align-items: center;
                              gap: 8px;
                              left: -2px;
                              position: relative;
                              width: 100%;
                              font-size: 14px;
                              font-family: ${styleConstants.fontFamilies
                                .regular};
                              color: ${themeConstants.colors.cta};
                            `}
                          >
                            <SvgRecurring height={16} width={16} />
                            {translate('Reactivate Subscription')}
                          </div>
                        ),
                        ariaLabelSource: 'Reactivate Subscription',
                        onClick: handleRenewSubscription,
                      },
                    ]
                  : []),
              ],
              openComponent: (
                <span
                  css={css`
                    color: ${themeConstants.colors.cta};
                  `}
                >
                  <SvgChevronUpLoD />
                </span>
              ),
              closedComponent: (
                <span
                  css={css`
                    color: ${themeConstants.colors.cta};
                  `}
                >
                  <SvgChevronDownLoD />
                </span>
              ),
            }}
          />
        )}
      </div>

      {currentTier !== 'STARTER' && organizationSubscription?.subscription && (
        <Box padding={spacing(5)} outlineBorder noShadow>
          <div className={'flex-column gap-5'}>
            <div className={'flex-row gap-5 justify-between align-center'}>
              <span className={'font-h3'}>
                {translate('${recurrence} Invoice', {
                  recurrence: getBillingCycleLabel(
                    organizationSubscription.subscription.billingCycle,
                  ),
                })}
              </span>
              <Button
                variant={'toolbar'}
                dropShadow={false}
                onClick={(): void => {
                  // TODO: change this to a more direct deeplink to billing history when available from backend
                  putManageSubscription({
                    orgId: organizationId,
                    redirect: true,
                    body: {},
                  })
                }}
              >
                {translate('View')}
              </Button>
            </div>
            <div className={'divider'} />
            <div className={'flex-column'}>
              <div className={'details-row'}>
                <span className={'font-md text-secondary'}>
                  {translate('Next Invoice Date')}
                </span>
                <span className={'font-md'}>
                  {organizationSubscription.subscription.state.status ===
                  SubscriptionStatus.ACTIVE
                    ? moment(
                        organizationSubscription.subscription.state
                          .nextBillingAt,
                      ).format('MMMM D, YYYY')
                    : translate('N/A')}
                </span>
              </div>
            </div>
          </div>
        </Box>
      )}
    </div>
  )
}

export default ManageSubscriptionHeader
